import { FetchBaseQueryError } from '@reduxjs/toolkit/query'
import { SerializedError } from '@reduxjs/toolkit'
import { DateTime } from 'luxon'

import { PostTimelineTypes } from './schema/Time'
import { CustomError } from './types'
import { within } from '@testing-library/react'

const dateTimeOptions = {
  weekday: 'long',
  month: 'long',
  day: 'numeric',
  year: 'numeric',
  hour: 'numeric',
  minute: '2-digit',
  hour12: true,
}

export const containsAnySpecialCharacter = (input: string): boolean => {
  const specialCharRegex = /[^a-zA-Z0-9]/
  return specialCharRegex.test(input)
}

export const stringShortener = (str: string, length: number): string => {
  if (str.length <= length) {
    return str
  }
  return str.slice(0, length) + '...'
}

export const isFetchBaseQueryError = (
  error: unknown,
): error is FetchBaseQueryError => {
  return typeof error === 'object' && error != null && 'status' in error
}

export const isErrorWithMessage = (
  error: unknown,
): error is { message: string } => {
  return (
    typeof error === 'object' &&
    error != null &&
    'message' in error &&
    typeof (error as any).message === 'string'
  )
}

export const isSerializedError = (error: unknown): error is SerializedError => {
  return (
    typeof error === 'object' &&
    error != null &&
    'name' in error &&
    typeof (error as any).message === 'string' &&
    typeof (error as any).stack === 'string' &&
    typeof (error as any).code === 'string'
  )
}

export const errorHandler = (error: unknown): CustomError | undefined => {
  console.log(error)
  if (isFetchBaseQueryError(error)) {
    if (error.status === 'FETCH_ERROR') {
      return {
        errorCode: 500,
        error: error,
      }
    }
    if (error.status === 'PARSING_ERROR') {
      if (error.originalStatus === 429) {
        return { errorCode: 429, error: error }
      }
      return {
        errorCode: 500,
        error: error,
      }
    }
    if (error.status === 'CUSTOM_ERROR') {
      return {
        errorCode: 500,
        error: error,
      }
    }
    if (error.status === 'TIMEOUT_ERROR') {
      return {
        errorCode: 500,
        error: error,
      }
    }
    return {
      errorCode: error.status,
      error: error,
    }
  } else if (isErrorWithMessage(error)) {
    return {
      errorCode: 500,
      error: error,
    }
  } else if (isSerializedError(error)) {
    return {
      errorCode: Number(error.code) | 500,
      error: error,
    }
  }
}

export const dateFormatter = (
  dateString: string,
  type: string,
): string | PostTimelineTypes | null => {
  // these are all DATES
  if (type === 'createdAt') {
    return new Intl.DateTimeFormat('en-US', {
      year: dateTimeOptions.year,
      month: dateTimeOptions.month,
      day: dateTimeOptions.day,
    } as Intl.DateTimeFormatOptions).format(new Date(dateString))
  }

  if (type === 'createdAtShort') {
    return new Intl.DateTimeFormat('en-US', {
      year: dateTimeOptions.year,
      month: dateTimeOptions.month,
    } as Intl.DateTimeFormatOptions).format(new Date(dateString))
  }

  if (type === 'dateOfBirth') {
    return new Intl.DateTimeFormat('en-US', {
      year: dateTimeOptions.year,
      month: dateTimeOptions.month,
      day: dateTimeOptions.day,
    } as Intl.DateTimeFormatOptions).format(new Date(dateString))
  }

  if (type === 'dateOfBirthShort') {
    return new Intl.DateTimeFormat('en-US', {
      year: dateTimeOptions.year,
      month: dateTimeOptions.month,
    } as Intl.DateTimeFormatOptions).format(new Date(dateString))
  }

  if (type === 'dateOfBirthNum') {
    return new Intl.DateTimeFormat('en-US').format(new Date(dateString))
  }

  // only POST has TIME and DATE
  if (type === 'post') {
    const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone
    let timeline: PostTimelineTypes = {
      withinTheYear: true,
      short: '',
      long: '',
      time: '',
    }

    const now = DateTime.now().setZone(userTimeZone)
    const given = DateTime.fromISO(dateString, { zone: 'utc' }).setZone(
      userTimeZone,
    )
    const isToday = now.toISODate() === given.toISODate()
    const withinTheYear = now.year === given.year

    if (isToday) {
      // isToday && Ns | Ns | Ns (N is number)
      const diffInSeconds = Math.floor(now.diff(given, 'seconds').seconds)

      if (diffInSeconds < 60) {
        timeline.short = `${diffInSeconds < 0 ? 0 : diffInSeconds}s`
      } else if (diffInSeconds < 3600) {
        timeline.short = `${Math.floor(diffInSeconds / 60)}m`
      } else {
        timeline.short = `${Math.floor(diffInSeconds / 3600)}h`
      }
    } else {
      // !isToday && 'm dd'
      timeline.short = given.toFormat('MMM d')
    }

    if (!withinTheYear) {
      timeline.withinTheYear = false
    }

    timeline.long = given.toFormat('MMM d, yyyy')
    timeline.time = given.toFormat('h:mm a')

    return timeline
  }

  return null
}

export const dateInputFormatter = (date?: string): string => {
  if (!date) return ''
  const [month, day, year] = date.split('/')

  const numDay = Number(day)
  const numMon = Number(month)
  const numYear = Number(year)
  const paddedDay = numDay >= 1 && numDay < 10 ? `0${numDay}` : numDay
  const paddedMonth = numMon >= 1 && numMon < 10 ? `0${numMon}` : numMon
  const paddedYear =
    numYear >= 1 && numYear < 10
      ? `000${numYear}`
      : numYear >= 10 && numYear < 100
        ? `00${numYear}`
        : numYear >= 100 && numYear < 1000
          ? `0${numYear}`
          : numYear

  return `${paddedYear}-${paddedMonth}-${paddedDay}`
}
